<template>
  <div>
    <div class="sc-card rounded-xl overflow-hidden" :class="{ 'sc-card--static': hideEdit }">
    <div class="relative px-5 py-4">
      <div v-if="sr" class="flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between" :class="hasToolbar ? 'mb-2' : 'mb-3'" @click="goToCenterReport">
          <div class="flex flex-col gap-0.5">
            <h2 class="text-[12px] font-bold uppercase tracking-[0.06em] text-foreground">{{ title }}</h2>
            <div v-if="sr.data && !isLoading" class="flex items-center gap-1.5">
              <CalendarDaysIcon class="w-2.5 h-2.5 text-muted-foreground/70" />
              <span class="text-[10px] text-muted-foreground/80">
                {{ sr.date.formatBoundaries() }}<template v-if="sr.data.previous_intervals"> · vs {{ sr.previousDate.formatBoundaries() }}</template>
              </span>
            </div>
          </div>
          <Cog6ToothIcon v-if="!hideEdit" class="w-4 h-4 text-muted-foreground/70" @click.stop="goToEdit" />
        </div>

        <div>
          <!-- Controls zone -->
          <div v-if="hasToolbar" class="mb-3">
            <slot name="toolbar" />
          </div>
          <div v-if="showChart" class="flex items-center mb-2" :class="hasToolbar ? 'justify-end' : ''">
            <TogglePill v-model="isCumulative">
              <template #icon>
                <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2 3h10M2 7l3 4h7M2 11h0" />
                </svg>
              </template>
              {{ $t('chart.cumulative_data') }}
            </TogglePill>
          </div>

          <div v-if="!isLoading">
            <SalesEvolution v-if="sr.data && showEvolution" :title="''"
              :center="center" :last-update="null" :current="currentAmount"
              :previous="previousAmount" :loading="false" />

            <div v-if="showChart">
              <Transition name="fade">
                <CumulatedSalesChart :center-id="centerID"
                  :chart-id="`sales-card-${props.center}-${props.component}`"
                  :boundaries="boundaries" :current-data="truncatedCurrentData"
                  :previous-data="truncatedPreviousData" :x-axis="xAxisKeys"
                  :chart-type="isCumulative ? 'line' : 'bar'"
                  :projection-data="projectionResult?.projected"
                  :cutoff-index="projectionResult?.cutoff" />
              </Transition>
            </div>
          </div>

          <div v-else class="flex justify-center items-center py-2">
            <div v-if="sr?.serverError"
              class="flex flex-col items-center text-gray-500">
              <ExclamationTriangleIcon class="w-8 h-8 mb-2 text-amber-500" />
              <p class="text-xs">{{ $t('components.salesreport.server_error') }}</p>
            </div>
            <div v-else-if="sr?.disconnected"
              class="flex flex-col items-center text-gray-500"
              @click.stop="reconnectCenter">
              <DisconnectedIcon class="w-8 h-8 mb-2" />
              <p class="text-xs">{{ $t('components.salesreport.disconnected') }}
              </p>
              <button class="mt-2 text-xs text-blue-600 hover:text-blue-800">
                {{ $t('components.salesreport.reconnect') }}
              </button>
            </div>
            <Loading v-else />
          </div>
        </div>
      </div>
    </div>
  </div>

    <EditComponentSheet v-if="props.component && !hideEdit" v-model:isOpen="showEditSheet" :componentId="props.component" />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, useSlots } from 'vue';
import Loading from './Loading.vue';
import DisconnectedIcon from './icons/DisconnectedIcon.vue';

import SalesEvolution from './SalesEvolution.vue';
import { Cog6ToothIcon, CalendarDaysIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import EditComponentSheet from './bottomsheet/EditComponentSheet.vue';
import { useSalesReportsStore } from '../stores/salesReports';
import { useNewComponentStore } from '../stores/newComponentStore';
import CumulatedSalesChart from './charts/CumulatedSalesChart.vue';
import TogglePill from './ui/TogglePill.vue';
import { useAppSettingsStore } from '../stores/AppSettingsStore';
import { dateUtils } from '@/utils/DateUtils';
import { useRouter } from 'vue-router';
import { computeProjection } from '@/utils/Projection';
import type { IntervalPoint } from '@/responses';
const salesReportsStore = useSalesReportsStore();
const newComponentStore = useNewComponentStore();
const router = useRouter();
const slots = useSlots();

const isCumulative = ref(false);
const showEditSheet = ref(false);
const hasToolbar = computed(() => !!slots.toolbar);

const props = withDefaults(defineProps<{
  reportIndex?: number
  center: number;
  component?: string;
  showChart?: boolean;
  title?: string;
  cumulated?: boolean;
  showEvolution?: boolean;
  showLabels?: boolean;
  hideEdit?: boolean;
  xAxisFormatter?: (dateString: string) => string;
  seasonalityData?: number[];
}>(), {
  cumulated: false,
  showEvolution: true,
  showLabels: true,
  hideEdit: false
})

const boundaries = computed(() => {
  if (sr.value) {
    return sr.value.getBoundaries();
  }
  return [];
})

const title = computed(() => {
  return props.title ?? sr.value?.title;
})

const centerID = computed(() => {
  const appStore = useAppSettingsStore()
  return props.center ?? appStore.centers[0]?.id;
})

const sr = computed(() => {
  if (props.reportIndex) {
    return getReportByIndex(props.reportIndex);
  }

  if (props.center && props.component) {
    return salesReportsStore.getReportForCenterAndComponent(props.center, props.component);
  }
})

const isLoading = computed(() => {
  return !sr.value?.data
})

const currentData = computed(() => {
  if (sr.value) {
    const data = isCumulative.value ?
      sr.value.getCurrentCumulatedIntervals() :
      sr.value.getCurrentIntervals();

    return data || [];
  }
  return [];
})

const previousData = computed(() => {
  if (sr.value && sr.value.data?.previous_intervals != null) {
    const data = isCumulative.value ?
      sr.value.getPreviousCumulatedIntervals() :
      sr.value.getPreviousIntervals();

    return data || [];

  }
  return [];
})

const isProjectionMode = computed(() =>
  isCumulative.value && (props.seasonalityData?.length ?? 0) > 0
);

const fullCurrentCumulative = computed(() => {
  if (!sr.value?.data?.intervals) return [];
  const taxKey = sr.value.getTaxKey();
  return sr.value.data.intervals.map(
    (interval: IntervalPoint) => interval.data.cumulative.tax_data[taxKey]
  );
});

const projectionResult = computed(() => {
  if (!isProjectionMode.value || fullCurrentCumulative.value.length === 0) return null;
  return computeProjection({
    currentCumulative: fullCurrentCumulative.value,
    n1Cumulative: props.seasonalityData,
  });
});

const xAxisKeys = computed(() => {
  if (!sr.value) return [];

  if (isProjectionMode.value && sr.value.data) {
    const formatter = props.xAxisFormatter
      ? (i: { date: string }) => props.xAxisFormatter!(i.date)
      : (i: { date: string }) => dateUtils.formatDateForReportType(new Date(i.date), sr.value?.type || 'monthly');

    // Use ALL intervals (full period, not truncated)
    if (sr.value.data.previous_intervals && sr.value.data.previous_intervals.length >= sr.value.data.intervals.length) {
      return sr.value.data.previous_intervals.map(formatter);
    }
    return sr.value.data.intervals.map(formatter);
  }

  // Default (non-projection) behavior
  const formatter = props.xAxisFormatter
    ? (i: { date: string }) => props.xAxisFormatter!(i.date)
    : (i: { date: string }) => dateUtils.formatDateForReportType(new Date(i.date), sr.value?.type || 'monthly');

  if (sr.value.data?.previous_intervals) {
    return truncatedPreviousIntervals.value.map(formatter) || [];
  }
  return truncatedCurrentIntervals.value.map(formatter) || [];
})

const dataRange = computed(() => {
  if (sr.value) {
    const currentRange = sr.value.getIndexRange(true);
    const previousRange = sr.value.data?.previous_intervals ? sr.value.getIndexRange(false) : currentRange;
    return {
      start: Math.min(currentRange.start, previousRange.start),
      end: Math.max(currentRange.end, previousRange.end) + 1
    }
  }

  return {
    start: undefined,
    end: undefined
  }
})

// On raw data to keep keys before transforming into Dates
const truncatedPreviousIntervals = computed(() => {
  if (!sr.value?.data?.previous_intervals) return []
  return sr.value.data.previous_intervals.slice(dataRange.value.start, dataRange.value.end);
})

// On raw data to keep keys before transforming into Dates
const truncatedCurrentIntervals = computed(() => {
  if (!sr.value?.data?.intervals) return []
  return sr.value.data.intervals.slice(dataRange.value.start, dataRange.value.end);
})

const truncatedCurrentData = computed<(number | null)[]>(() => {
  if (isProjectionMode.value && projectionResult.value) {
    const { cutoff } = projectionResult.value;
    const full = fullCurrentCumulative.value;
    return full.map((v, i) => i < cutoff ? v : null);
  }
  // existing logic unchanged
  if (!currentData.value) return [];
  return currentData.value.slice(dataRange.value.start, dataRange.value.end);
})

const truncatedPreviousData = computed(() => {
  if (isProjectionMode.value) {
    // Full previous cumulative, no slicing
    if (sr.value && sr.value.data?.previous_intervals != null) {
      const data = sr.value.getPreviousCumulatedIntervals();
      return data || [];
    }
    return [];
  }
  // existing logic unchanged
  if (!previousData.value) return [];
  return previousData.value.slice(dataRange.value.start, dataRange.value.end);
})


const getReportByIndex = (index: number) => {
  if (index < 0) {
    return newComponentStore.newComponent;
  }
  if (!salesReportsStore.salesReports) {
    return null;
  }
  return salesReportsStore.salesReports[index];
}

const previousAmount = computed(() => {
  if (sr.value) {
    return sr.value.getEquitableTotal()
  }
  return 0;
})

const currentAmount = computed(() => {
  if (sr.value) {
    return sr.value.getCurrentTotal()
  }

  return 0;
})


const goToCenterReport = () => {
  // Don't navigate if the sales report is in error state
  if (sr.value?.disconnected || sr.value?.serverError) {
    return;
  }

  router.push({
    name: 'CenterReport',
    params: {
      id: props.component,
      centerId: props.center
    }
  })
}

const goToEdit = () => {
  showEditSheet.value = true
}

const reconnectCenter = () => {
  // Navigate to center signin page to re-authenticate
  router.push({
    name: 'CenterSignin'
  });
}

onMounted(async () => {
  if (!sr.value?.data) {
    await sr.value?.fetchData(centerID.value)
  }
})

</script>

<style scoped>
.sc-card {
  background: var(--card);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.03),
    0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.15s ease;
}
.sc-card:active {
  transform: scale(0.985);
}
.sc-card--static {
  transition: none;
}
.sc-card--static:active {
  transform: none;
}

:is(.dark *).sc-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.2),
    0 4px 12px rgba(0, 0, 0, 0.3);
}

</style>